'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { use, useState } from 'react'

import ViverseWorldCard from '@/components/ViverseWorldCard'
import SectionSeparator from '@/components/SectionSeparator'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useDraggingScroll } from '@/hooks/useDraggingScroll'
import { useScrollable } from '@/hooks/useScrollable'
import { cn } from '@/lib/utils'
import { Room } from '@/services/cms'
import { getCardType } from '@/lib/viverse-world-card'

type UserRoomsProps = {
  featuredRoomsPromise: Promise<Room[]>
}

function UserFeaturedRooms({ featuredRoomsPromise }: UserRoomsProps) {
  const featuredRooms = use(featuredRoomsPromise)

  const t = useTranslations()
  const { store } = useConfigsContext()

  const { containerRef, canScrollLeft, canScrollRight, scrollLeft, scrollRight } =
    useScrollable(436)

  const { bind, isDragging, onMouseDown, onMouseUp } = useDraggingScroll(containerRef)

  const [isHovered, setIsHovered] = useState(false)
  const htcToken = store?.credentials?.htcToken

  if (featuredRooms.length === 0) {
    return null
  }

  return (
    <>
      <SectionSeparator />
      <section
        id="featured"
        data-section="profile-featured-worlds"
        className="flex scroll-mt-36 flex-col justify-between px-4 md:px-8 lg:scroll-mt-40 lg:px-18"
      >
        {featuredRooms.length > 0 && (
          <div
            className="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <h3 className="subheader-2 lg:header-4 text-vive-white! lg:text-vive-white! mb-6">
              {t('user-worlds.sort-option.featured')}
            </h3>

            <div
              {...bind()}
              ref={containerRef}
              className="no-scrollbar -my-2 w-full overflow-x-auto scroll-smooth py-2 active:cursor-grabbing md:-mx-2"
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
            >
              <div className="flex w-full gap-4 md:gap-6 lg:gap-2">
                {featuredRooms.map((room, index) => (
                  <ViverseWorldCard
                    className="min-w-[322px]"
                    key={`world-card-${room.id}-${index}`}
                    info={room}
                    hideLiked={!htcToken}
                    imageClassName={cn({
                      'pointer-events-none cursor-grabbing': isDragging,
                      'pointer-events-auto cursor-pointer': !isDragging,
                    })}
                    hidePremiumBadge={false}
                    cardType={getCardType(room.content_type)}
                  />
                ))}
              </div>
            </div>

            <div
              className={cn(
                'group bg-vive-primary-70 absolute top-1/2 left-5 z-floating hidden size-8 -translate-y-1/2 cursor-pointer place-items-center rounded-full opacity-0 transition-opacity duration-300 md:grid md:size-[52px]',
                {
                  'opacity-100': isHovered && canScrollLeft,
                  'opacity-0': !canScrollLeft,
                },
              )}
              onClick={scrollLeft}
            >
              <ChevronLeft className="stroke-vive-primary-30 group-hover:stroke-vive-primary-0 size-[36px] transition-all" />
            </div>

            <div
              className={cn(
                'group bg-vive-primary-70 absolute top-1/2 right-5 z-floating hidden size-8 -translate-y-1/2 cursor-pointer place-items-center rounded-full opacity-0 transition-opacity duration-300 md:grid md:size-[52px]',
                {
                  'opacity-100': isHovered && canScrollRight,
                  'opacity-0': !canScrollRight,
                },
              )}
              onClick={scrollRight}
            >
              <ChevronRight
                onClick={scrollRight}
                className="stroke-vive-primary-30 group-hover:stroke-vive-primary-0 size-[36px] transition-all"
              />
            </div>
          </div>
        )}
      </section>
    </>
  )
}

export default UserFeaturedRooms
